import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { format, parseISO } from 'date-fns'
import { businessToday } from '../lib/dates'
import {
  RefreshCw, Plus, Trash2, Edit2, Loader2, X, Package2,
  ArrowDownToLine, ArrowUpFromLine, RotateCcw, Clock, AlertCircle,
  Megaphone, Container, ShoppingCart,
} from 'lucide-react'
import {
  getWarehouseRates, updateWarehouseRates, recalculateWarehouseFees,
  getWarehouseActivities, createWarehouseActivity,
  updateWarehouseActivity, deleteWarehouseActivity,
  syncWarehouseFromSlack,
} from '../lib/api'
import { ToastContainer, useToast } from './Toast'

function formatCurrency(v) {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 2 }).format(v ?? 0)
}

const ACTIVITY_TYPES = [
  { value: 'goods_in',   label: 'Goods Received',     icon: ArrowDownToLine, color: 'text-green-600',  bg: 'bg-green-50'  },
  { value: 'package_in', label: 'Package Received',   icon: Package2,        color: 'text-blue-600',   bg: 'bg-blue-50'   },
  { value: 'return',     label: 'Return',             icon: RotateCcw,       color: 'text-amber-600',  bg: 'bg-amber-50'  },
  { value: 'goods_out',  label: 'Goods Out',          icon: ArrowUpFromLine, color: 'text-purple-600', bg: 'bg-purple-50' },
  { value: 'time',       label: 'Time',               icon: Clock,           color: 'text-slate-600',  bg: 'bg-slate-100' },
  { value: 'marketing',  label: 'Marketing Services', icon: Megaphone,       color: 'text-pink-600',   bg: 'bg-pink-50'   },
  { value: 'container_20ft', label: '20ft Container',  icon: Container,       color: 'text-teal-600',   bg: 'bg-teal-50'   },
  { value: 'container_40ft', label: '40ft Container',  icon: Container,       color: 'text-cyan-700',   bg: 'bg-cyan-50'   },
  // Pass-through cost. Unlike every type above there is no rate card entry —
  // the amount is typed into the Fee field and quantity stays 1, because a
  // purchase is whatever that invoice cost rather than a repeatable unit rate.
  { value: 'purchase',   label: 'Purchase (cost)',    icon: ShoppingCart,    color: 'text-indigo-600', bg: 'bg-indigo-50' },
]

function activityMeta(type) {
  return ACTIVITY_TYPES.find(t => t.value === type) || ACTIVITY_TYPES[0]
}

// ── Rates card ──────────────────────────────────────────────────────────────

function RatesCard({ clientId, onFeesRecalculated }) {
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState(null)
  const [recalculating, setRecalculating] = useState(false)
  const queryClient = useQueryClient()
  const { toasts, addToast, removeToast } = useToast()

  const { data: rates, isLoading, error: ratesError } = useQuery({
    queryKey: ['warehouse', 'rates', clientId],
    queryFn: () => getWarehouseRates(clientId),
    onSuccess: (d) => { if (!form) setForm(d) },
  })

  // sync form when rates load
  if (rates && !form) {
    setForm({ ...rates })
  }

  const mutation = useMutation({
    mutationFn: (payload) => updateWarehouseRates(clientId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['warehouse', 'rates', clientId] })
      queryClient.invalidateQueries({ queryKey: ['warehouse', 'activities', clientId] })
      setEditing(false)
      // Deliberately narrow wording: update_rates only backfills activities
      // whose fee is still 0, and skips any month already invoiced. It does
      // NOT re-price existing fees — the Recalculate button does that.
      addToast({ type: 'success', message: 'Rates saved. Unpriced activities backfilled.' })
    },
    onError: (err) => addToast({ type: 'error', message: err.response?.data?.detail || 'Failed to save rates.' }),
  })

  async function handleRecalculate() {
    setRecalculating(true)
    try {
      const result = await recalculateWarehouseFees(clientId)
      queryClient.invalidateQueries({ queryKey: ['warehouse', 'activities', clientId] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'clients'] })
      // skipped_locked matters: a run that changes nothing because every row
      // sits in an invoiced month is a refusal, not a no-op, and saying
      // "0 activities" would read as a failure.
      const n = result.recalculated
      const held = result.skipped_locked || 0
      addToast({
        type: 'success',
        message: `Recalculated fees for ${n} activit${n === 1 ? 'y' : 'ies'}.`
          + (held ? ` ${held} left untouched in already-invoiced months.` : ''),
        duration: held ? 6000 : undefined,
      })
    } catch (err) {
      addToast({ type: 'error', message: err.response?.data?.detail || 'Recalculation failed.' })
    } finally {
      setRecalculating(false)
    }
  }

  function handleSave(e) {
    e.preventDefault()
    mutation.mutate({
      goods_in_fee:               form.goods_in_fee               !== '' ? Number(form.goods_in_fee)               : 0,
      package_in_fee:             form.package_in_fee             !== '' ? Number(form.package_in_fee)             : 0,
      goods_out_fee:              form.goods_out_fee              !== '' ? Number(form.goods_out_fee)              : 0,
      return_base_fee:            form.return_base_fee            !== '' ? Number(form.return_base_fee)            : 0,
      return_additional_item_fee: form.return_additional_item_fee !== '' ? Number(form.return_additional_item_fee) : 0,
      time_fee:                   form.time_fee                   !== '' ? Number(form.time_fee)                   : 0,
      marketing_fee:              form.marketing_fee              !== '' ? Number(form.marketing_fee)              : 0,
      container_20ft_fee:         form.container_20ft_fee         !== '' ? Number(form.container_20ft_fee)         : 0,
      container_40ft_fee:         form.container_40ft_fee         !== '' ? Number(form.container_40ft_fee)         : 0,
    })
  }

  const RATE_FIELDS = [
    { key: 'goods_in_fee',               label: 'Goods In Fee',                  hint: 'Per pallet / unit received' },
    { key: 'package_in_fee',             label: 'Package In Fee',                hint: 'Per package / parcel received' },
    { key: 'goods_out_fee',              label: 'Goods Out Fee',                 hint: 'Per unit dispatched / shipped out' },
    { key: 'return_base_fee',            label: 'Return Base Fee',               hint: 'Base charge per return' },
    { key: 'return_additional_item_fee', label: 'Return Additional Item Fee',    hint: 'Per extra item (beyond first)' },
    { key: 'time_fee',                   label: 'Time Fee',                      hint: 'Per hour of warehouse time' },
    { key: 'marketing_fee',              label: 'Marketing Services Fee',        hint: 'Per unit / hour / deliverable' },
    { key: 'container_20ft_fee',         label: '20ft Container Fee',            hint: 'Per 20ft container' },
    { key: 'container_40ft_fee',         label: '40ft Container Fee',            hint: 'Per 40ft container' },
  ]

  return (
    <div className="card p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-700">Activity Rates</h3>
        {!editing && (
          <div className="flex gap-2">
            <button onClick={handleRecalculate} disabled={recalculating} className="btn-secondary text-xs py-1 px-3">
              {recalculating ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
              Recalculate
            </button>
            <button onClick={() => setEditing(true)} className="btn-secondary text-xs py-1 px-3">
              <Edit2 size={12} /> Edit Rates
            </button>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="skeleton h-8 rounded" />
      ) : editing ? (
        <form onSubmit={handleSave}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            {RATE_FIELDS.map(({ key, label, hint }) => (
              <div key={key}>
                <label className="label">{label} ($)</label>
                <input
                  type="number" min="0" step="0.01"
                  value={form?.[key] ?? ''}
                  onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
                  className="input" placeholder="0.00"
                />
                <p className="text-xs text-slate-400 mt-1">{hint}</p>
              </div>
            ))}
          </div>
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => { setEditing(false); setForm({ ...rates }) }} className="btn-secondary text-xs" disabled={mutation.isPending}>Cancel</button>
            <button type="submit" className="btn-primary text-xs" disabled={mutation.isPending}>
              {mutation.isPending ? <Loader2 size={12} className="animate-spin" /> : null}
              Save Rates
            </button>
          </div>
        </form>
      ) : ratesError ? (
        // Without this branch a failed rates fetch renders nothing at all, which
        // reads as "no rates set" — and the Edit Rates form would then open on
        // blank inputs and save every fee as 0.
        <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2">
          <AlertCircle size={14} className="text-red-500 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-xs font-semibold text-red-700">Couldn't load activity rates</p>
            <p className="text-xs text-red-600 mt-0.5">
              This is a load failure, not an empty rate card — don't edit rates until it loads.
            </p>
            <p className="text-xs text-red-500/80 mt-0.5 font-mono break-all">
              {ratesError?.response?.data?.detail || ratesError?.message || 'Unknown error'}
            </p>
          </div>
        </div>
      ) : rates ? (
        <div className="flex flex-wrap gap-5 text-sm">
          {RATE_FIELDS.map(({ key, label }) => (
            <div key={key}>
              <span className="text-slate-500">{label}:</span>{' '}
              <span className="font-medium">{formatCurrency(rates[key])}</span>
            </div>
          ))}
        </div>
      ) : null}
      <ToastContainer toasts={toasts} onClose={removeToast} />
    </div>
  )
}

// ── Activity form modal ──────────────────────────────────────────────────────

function ActivityModal({ clientId, activity, onClose, onSuccess }) {
  const today = businessToday()
  const [form, setForm] = useState(activity ? {
    activity_type: activity.activity_type,
    quantity:      String(activity.quantity),
    reference:     activity.reference ?? '',
    notes:         activity.notes ?? '',
    activity_date: activity.activity_date,
    fee:           '',   // leave blank — fee override only set if user explicitly enters one
  } : {
    activity_type: 'goods_in',
    quantity:      '1',
    reference:     '',
    notes:         '',
    activity_date: today,
    fee:           '',
  })
  const { toasts, addToast, removeToast } = useToast()

  const mutation = useMutation({
    mutationFn: (payload) => activity
      ? updateWarehouseActivity(activity.id, payload)
      : createWarehouseActivity({ ...payload, client_id: clientId }),
    onSuccess: () => { onSuccess() },
    onError: (err) => addToast({ type: 'error', message: err.response?.data?.detail || 'Failed to save.' }),
  })

  function handleSubmit(e) {
    e.preventDefault()
    mutation.mutate({
      activity_type: form.activity_type,
      quantity:      parseFloat(form.quantity) || 1,
      reference:     form.reference || null,
      notes:         form.notes || null,
      activity_date: form.activity_date,
      fee:           form.fee !== '' ? Number(form.fee) : undefined,
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h3 className="font-semibold text-slate-800">{activity ? 'Edit Activity' : 'Add Activity'}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div>
            <label className="label">Type</label>
            <select
              value={form.activity_type}
              onChange={e => setForm(p => ({ ...p, activity_type: e.target.value }))}
              className="input"
            >
              {ACTIVITY_TYPES.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Quantity</label>
              <input type="number" min="0" step="0.01" value={form.quantity}
                onChange={e => setForm(p => ({ ...p, quantity: e.target.value }))}
                className="input" />
            </div>
            <div>
              <label className="label">Date</label>
              <input type="date" value={form.activity_date}
                onChange={e => setForm(p => ({ ...p, activity_date: e.target.value }))}
                className="input" />
            </div>
          </div>
          <div>
            <label className="label">Reference / Order #</label>
            <input type="text" value={form.reference}
              onChange={e => setForm(p => ({ ...p, reference: e.target.value }))}
              className="input" placeholder="e.g. NV10045" />
          </div>
          <div>
            <label className="label">Notes</label>
            <input type="text" value={form.notes}
              onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
              className="input" placeholder="Optional details" />
          </div>
          <div>
            {/* A purchase has no rate card entry, so "leave blank to
                auto-calculate" would quietly bill $0 — the field is the
                charge, not an override. Say so, and require it. */}
            <label className="label">
              {form.activity_type === 'purchase' ? 'Cost ($)' : 'Fee Override ($)'}
            </label>
            <input type="number" min="0" step="0.01" value={form.fee}
              onChange={e => setForm(p => ({ ...p, fee: e.target.value }))}
              required={form.activity_type === 'purchase'}
              className="input"
              placeholder={form.activity_type === 'purchase'
                ? 'What this purchase cost'
                : 'Leave blank to auto-calculate'} />
            <p className="text-xs text-slate-400 mt-1">
              {form.activity_type === 'purchase'
                ? 'Required — this is the amount charged. There is no rate to fall back on.'
                : 'Blank = auto-calculated from rates'}
            </p>
          </div>
          <div className="flex gap-3 justify-end pt-1">
            <button type="button" onClick={onClose} className="btn-secondary" disabled={mutation.isPending}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={mutation.isPending}>
              {mutation.isPending ? <Loader2 size={14} className="animate-spin" /> : null}
              {activity ? 'Save Changes' : 'Add Activity'}
            </button>
          </div>
        </form>
        <ToastContainer toasts={toasts} onClose={removeToast} />
      </div>
    </div>
  )
}

// ── Main tab ─────────────────────────────────────────────────────────────────

export default function WarehouseTab({ clientId, hasSlackChannel }) {
  const [showModal, setShowModal]   = useState(false)
  const [editing, setEditing]       = useState(null)
  const [fromDate, setFromDate]     = useState(() => {
    const d = new Date()
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
  })
  const [toDate, setToDate]         = useState('')
  const [syncing, setSyncing]               = useState(false)
  const [fullSyncing, setFullSyncing]       = useState(false)
  const [unrecognisedSamples, setUnrecognisedSamples] = useState([])
  const [showUnrecognised, setShowUnrecognised]       = useState(false)
  const queryClient                  = useQueryClient()
  const { toasts, addToast, removeToast } = useToast()

  const { data: activities = [], isLoading, error: activitiesError } = useQuery({
    queryKey: ['warehouse', 'activities', clientId, fromDate, toDate],
    queryFn: () => getWarehouseActivities(clientId, { ...(fromDate && { from: fromDate }), ...(toDate && { to: toDate }) }),
    enabled: !!clientId,
  })

  const deleteMutation = useMutation({
    mutationFn: deleteWarehouseActivity,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['warehouse', 'activities', clientId] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'clients'] })
      addToast({ type: 'success', message: 'Activity deleted.' })
    },
    onError: () => addToast({ type: 'error', message: 'Failed to delete.' }),
  })

  async function handleSync(full = false) {
    if (full) setFullSyncing(true)
    else setSyncing(true)
    try {
      const result = await syncWarehouseFromSlack(clientId, { fullSync: full })
      queryClient.invalidateQueries({ queryKey: ['warehouse', 'activities', clientId] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'clients'] })
      const samples = result.unrecognised_samples || []
      setUnrecognisedSamples(samples)
      setShowUnrecognised(samples.length > 0)
      const unrecMsg = result.unrecognised > 0 ? ` (${result.unrecognised} unrecognised)` : ''
      addToast({
        type: result.activities_created > 0 ? 'success' : 'default',
        message: `Sync complete — ${result.activities_created} new activit${result.activities_created === 1 ? 'y' : 'ies'} from ${result.messages_scanned} messages. ${result.already_synced} already synced.${unrecMsg}`,
        duration: 8000,
      })
    } catch (err) {
      addToast({ type: 'error', message: err.response?.data?.detail || 'Sync failed.' })
    } finally {
      setSyncing(false)
      setFullSyncing(false)
    }
  }

  function handleModalSuccess() {
    queryClient.invalidateQueries({ queryKey: ['warehouse', 'activities', clientId] })
    queryClient.invalidateQueries({ queryKey: ['admin', 'clients'] })
    setShowModal(false)
    setEditing(null)
    addToast({ type: 'success', message: 'Activity saved.' })
  }

  // MTD summary
  const mtdTotal    = activities.reduce((s, a) => s + (a.fee || 0), 0)
  const byType      = ACTIVITY_TYPES.map(t => ({
    ...t,
    count: activities.filter(a => a.activity_type === t.value).length,
    total: activities.filter(a => a.activity_type === t.value).reduce((s, a) => s + (a.fee || 0), 0),
  })).filter(t => t.count > 0)

  return (
    <div className="space-y-4">
      <RatesCard clientId={clientId} />

      {/* Summary strip */}
      {byType.length > 0 && (
        <div className="card p-4">
          <div className="flex flex-wrap gap-5">
            {byType.map(t => {
              const Icon = t.icon
              return (
                <div key={t.value} className="flex items-center gap-2">
                  <span className={`flex items-center justify-center w-7 h-7 rounded-full ${t.bg}`}>
                    <Icon size={14} className={t.color} />
                  </span>
                  <div>
                    <p className="text-xs text-slate-500">{t.label}</p>
                    <p className="text-sm font-semibold text-slate-800">
                      {t.count}× <span className="font-normal text-slate-500">{formatCurrency(t.total)}</span>
                    </p>
                  </div>
                </div>
              )
            })}
            <div className="ml-auto flex items-end">
              <div className="text-right">
                <p className="text-xs text-slate-500">Period Total</p>
                <p className="text-lg font-bold text-slate-900">{formatCurrency(mtdTotal)}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Activities table */}
      <div className="card overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-4 py-3 border-b border-slate-200">
          <h3 className="text-sm font-semibold text-slate-700">Activities</h3>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Date filters */}
            <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
              className="text-xs border border-slate-200 rounded-md px-2 py-1 text-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-400" />
            <span className="text-slate-400 text-xs">to</span>
            <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
              className="text-xs border border-slate-200 rounded-md px-2 py-1 text-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-400" />

            {hasSlackChannel && (
              <>
                <button onClick={() => handleSync(false)} disabled={syncing || fullSyncing} className="btn-secondary text-xs py-1 px-3">
                  {syncing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                  {syncing ? 'Syncing…' : 'Sync Slack'}
                </button>
                <button onClick={() => handleSync(true)} disabled={syncing || fullSyncing} className="btn-secondary text-xs py-1 px-3" title="Re-scan full channel history">
                  {fullSyncing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                  {fullSyncing ? 'Syncing…' : 'Full Sync'}
                </button>
              </>
            )}
            <button onClick={() => setShowModal(true)} className="btn-primary text-xs py-1 px-3">
              <Plus size={12} /> Add
            </button>
          </div>
        </div>

        {!hasSlackChannel && (
          <div className="px-4 py-2 bg-amber-50 border-b border-amber-100 flex items-center gap-2">
            <AlertCircle size={13} className="text-amber-500 shrink-0" />
            <p className="text-xs text-amber-700">No Slack channel linked. Edit the client to connect a channel and enable auto-sync.</p>
          </div>
        )}

        {showUnrecognised && unrecognisedSamples.length > 0 && (
          <div className="border-b border-slate-200 bg-amber-50">
            <button
              onClick={() => setShowUnrecognised(false)}
              className="w-full flex items-center justify-between px-4 py-2 text-xs font-semibold text-amber-800 hover:bg-amber-100 transition-colors"
            >
              <span className="flex items-center gap-1.5">
                <AlertCircle size={13} className="text-amber-500" />
                {unrecognisedSamples.length} unrecognised message{unrecognisedSamples.length !== 1 ? 's' : ''} — click to dismiss
              </span>
              <X size={13} className="text-amber-500" />
            </button>
            <div className="px-4 pb-3 space-y-1 max-h-48 overflow-y-auto">
              {unrecognisedSamples.map((s, i) => (
                <div key={i} className="flex gap-3 text-xs">
                  <span className="text-amber-600 font-mono shrink-0">{s.date}</span>
                  <span className="text-amber-900 break-all">{s.text}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Type</th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Qty</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Reference</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Notes</th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Fee</th>
                <th className="w-16 px-3 py-3" />
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-100">
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>
                    {[1,2,3,4,5,6,7].map(j => (
                      <td key={j} className="px-4 py-3"><div className="skeleton h-4 rounded" /></td>
                    ))}
                  </tr>
                ))
              ) : activitiesError ? (
                /* A failed fetch leaves `activities` on its `= []` default, so the
                   summary strip disappears and the empty state below claims there
                   was no billable warehouse activity in the period. */
                <tr>
                  <td colSpan={7} className="py-12 text-center">
                    <AlertCircle size={28} className="mx-auto text-red-400 mb-2" />
                    <p className="text-red-600 text-sm font-medium">Couldn't load activities</p>
                    <p className="text-red-500 text-xs mt-1">
                      This is a load failure, not an empty period — activities may exist but aren't shown, and no period total is being calculated.
                    </p>
                    <p className="text-slate-400 text-xs mt-1 font-mono break-all">
                      {activitiesError?.response?.data?.detail || activitiesError?.message || 'Unknown error'}
                    </p>
                  </td>
                </tr>
              ) : activities.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center">
                    <Package2 size={28} className="mx-auto text-slate-300 mb-2" />
                    <p className="text-slate-400 text-sm">No activities in this period</p>
                    <p className="text-slate-300 text-xs mt-1">Add manually or sync from Slack</p>
                  </td>
                </tr>
              ) : activities.map(a => {
                const meta = activityMeta(a.activity_type)
                const Icon = meta.icon
                const isFromSlack = !!a.slack_ts
                return (
                  <tr key={a.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap text-xs">
                      {a.activity_date}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full ${meta.bg} ${meta.color}`}>
                        <Icon size={11} />
                        {meta.label}
                        {isFromSlack && <span className="opacity-50 ml-0.5" title="Synced from Slack">✦</span>}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-right text-slate-700 tabular-nums">{a.quantity}</td>
                    <td className="px-4 py-3 text-slate-700 font-mono text-xs">{a.reference || '—'}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs max-w-xs truncate">{a.notes || '—'}</td>
                    <td className="px-3 py-3 text-right font-semibold text-slate-900 tabular-nums">{formatCurrency(a.fee)}</td>
                    <td className="px-3 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => setEditing(a)}
                          className="p-1 rounded text-slate-300 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                          title="Edit"
                        >
                          <Edit2 size={13} />
                        </button>
                        <button
                          onClick={() => { if (window.confirm('Delete this activity?')) deleteMutation.mutate(a.id) }}
                          disabled={deleteMutation.isPending}
                          className="p-1 rounded text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {(showModal || editing) && (
        <ActivityModal
          clientId={clientId}
          activity={editing}
          onClose={() => { setShowModal(false); setEditing(null) }}
          onSuccess={handleModalSuccess}
        />
      )}

      <ToastContainer toasts={toasts} onClose={removeToast} />
    </div>
  )
}
